import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const QUICK_REACTIONS = ['👍', '❤️', '😂', '🔥', '😮', '👏'];
const EMOJI_GROUPS = [
  { label: '😊', name: 'Smileys', emojis: ['😀','😂','🥰','😍','🤩','😎','🤔','😅','😊','🙌','😁','🤣','😄','🥳','😏','🤗','😮','😭','🤯','🫠'] },
  { label: '👋', name: 'Gestures', emojis: ['👍','👎','🙏','👏','🤝','💪','✌️','🤞','👋','🫶','☝️','🤙','💅','🫡','✋','🤜','🤛','👌','🤌','💁'] },
  { label: '🔥', name: 'Symbols', emojis: ['❤️','🔥','✨','💯','🎉','🚀','⭐','🏆','💡','✅','🎯','💰','🤖','⚡','🌟','👀','💀','🎊','🥇','🫀'] },
  { label: '🌿', name: 'Nature', emojis: ['🌟','🌈','☀️','🌙','⛄','🌊','🌸','🍀','🐶','🦁','🐉','🦋','🌺','🍕','☕','🎵','🎮','📚','💻','🔑'] },
];

const ROLE_COLOR = { cwg: '#00d4ff', stakeholder: '#f59e0b', sme: '#a855f7', member: '#22c55e', intern: '#94a3b8' };
const ROLE_LABEL = { cwg: 'CWG', stakeholder: 'Stakeholder', sme: 'SME', member: 'Core', intern: 'Intern' };
const TIER_COLOR = { Legend: '#f59e0b', Veteran: '#a855f7', Core: '#22c55e', Probation: '#f97316', Intern: '#94a3b8' };

function formatTime(ts) {
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
function formatSidebarTime(ts) {
  if (!ts) return '';
  const d = new Date(ts), now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === new Date(now - 86400000).toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}
function getDateLabel(ts) {
  const d = new Date(ts), now = new Date();
  if (d.toDateString() === now.toDateString()) return 'Today';
  if (d.toDateString() === new Date(now - 86400000).toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
}
function needsDivider(msg, prev) {
  if (!prev) return true;
  return new Date(msg.ts).toDateString() !== new Date(prev.ts).toDateString();
}
function isGrouped(msg, prev) {
  if (!prev || msg.replyTo || prev.deleted) return false;
  if (msg.fromId !== prev.fromId) return false;
  return (new Date(msg.ts) - new Date(prev.ts)) < 5 * 60 * 1000;
}

function Avatar({ initials, avatarData, size = 34, color }) {
  const base = {
    width: size, height: size, borderRadius: '50%',
    background: color || '#1e293b',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: size * 0.35, fontWeight: 700, color: '#fff', flexShrink: 0,
    border: '1px solid rgba(255,255,255,0.1)',
    overflow: 'hidden',
  };
  if (avatarData?.startsWith('data:')) {
    return <div style={base}><img src={avatarData} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} /></div>;
  }
  if (avatarData) {
    return <div style={{ ...base, fontSize: size * 0.5 }}>{avatarData}</div>;
  }
  return <div style={base}>{initials || '??'}</div>;
}

function SidebarItem({ icon, initials, avatarData, name, sub, badge, active, onClick, time, iconColor }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 9, padding: '7px 10px',
        cursor: 'pointer',
        background: active ? 'rgba(0,212,255,0.1)' : hov ? 'rgba(255,255,255,0.04)' : 'transparent',
        borderLeft: `2px solid ${active ? 'var(--j1)' : 'transparent'}`,
      }}
    >
      <div style={{ flexShrink: 0 }}>
        {(initials || avatarData) ? (
          <Avatar initials={initials} avatarData={avatarData} size={32} color={iconColor} />
        ) : (
          <div style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17 }}>{icon}</div>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: badge > 0 ? 700 : 500, color: active ? 'var(--j1)' : badge > 0 ? 'var(--txt)' : 'var(--txt2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>{name}</span>
          {time && <span style={{ fontSize: 9, color: 'var(--txt3)', flexShrink: 0 }}>{time}</span>}
        </div>
        {sub && <div style={{ fontSize: 10, color: 'var(--txt3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</div>}
      </div>
      {badge > 0 && (
        <div style={{ background: 'var(--j6)', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 9, fontWeight: 700, flexShrink: 0, minWidth: 16, textAlign: 'center' }}>
          {badge > 99 ? '99+' : badge}
        </div>
      )}
    </div>
  );
}

function Section({ label, col, onToggle, children }) {
  return (
    <div>
      <div
        onClick={onToggle}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px 4px', cursor: 'pointer', userSelect: 'none' }}
      >
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--txt3)', textTransform: 'uppercase' }}>{label}</span>
        <span style={{ color: 'var(--txt3)', fontSize: 9 }}>{col ? '▶' : '▼'}</span>
      </div>
      {!col && children}
    </div>
  );
}

export default function Messages() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [channels, setChannels] = useState([]);
  const [recent, setRecent] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [showEmoji, setShowEmoji] = useState(false);
  const [emojiTab, setEmojiTab] = useState(0);
  const [hoveredId, setHoveredId] = useState(null);
  const [search, setSearch] = useState('');
  const [lastTs, setLastTs] = useState(null);
  const [collapsed, setCollapsed] = useState({ recent: false, channels: false, dms: false });

  const msgEndRef = useRef(null);
  const inputRef = useRef(null);
  const pollRef = useRef(null);

  const loadSidebar = useCallback(async () => {
    const [r1, r2, r3] = await Promise.allSettled([
      api.get('/messages/recent', { silent: true }),
      api.get('/messages/channels', { silent: true }),
      api.get('/messages/contacts', { silent: true }),
    ]);
    if (r1.status === 'fulfilled') setRecent(r1.value.data);
    if (r2.status === 'fulfilled') setChannels(r2.value.data);
    if (r3.status === 'fulfilled') setContacts(r3.value.data);
  }, []);

  useEffect(() => { loadSidebar(); }, [loadSidebar]);

  // Polling for new messages
  useEffect(() => {
    clearInterval(pollRef.current);
    if (!activeConv || !lastTs) return;
    pollRef.current = setInterval(async () => {
      try {
        const url = activeConv.type === 'channel'
          ? `/messages/channels/${activeConv.id}/since/${encodeURIComponent(lastTs)}`
          : `/messages/${activeConv.id}/since/${encodeURIComponent(lastTs)}`;
        const { data } = await api.get(url, { silent: true });
        if (data.length > 0) {
          setMessages(prev => {
            const ids = new Set(prev.map(m => m.id));
            const fresh = data.filter(m => !ids.has(m.id));
            if (!fresh.length) return prev;
            setLastTs(fresh[fresh.length - 1].ts);
            return [...prev, ...fresh];
          });
          loadSidebar();
        }
      } catch { }
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [activeConv, lastTs, loadSidebar]);

  useEffect(() => { msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const openConv = useCallback(async (type, id, data) => {
    setActiveConv({ type, id, data });
    setMessages([]);
    setReplyTo(null);
    setEditingId(null);
    setShowEmoji(false);
    setLoading(true);
    try {
      const url = type === 'channel' ? `/messages/channels/${id}` : `/messages/${id}`;
      const { data: msgs } = await api.get(url);
      setMessages(msgs);
      setLastTs(msgs.length ? msgs[msgs.length - 1].ts : new Date().toISOString());
      loadSidebar();
    } catch { } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [loadSidebar]);

  const sendMessage = useCallback(async (e) => {
    e?.preventDefault();
    const content = input.trim();
    if (!content || !activeConv || sending) return;
    setInput('');
    setSending(true);
    setShowEmoji(false);
    const reply = replyTo ? { id: replyTo.id, content: replyTo.content, fromName: replyTo.fromName || user.name } : null;
    const optimistic = {
      id: `opt-${Date.now()}`,
      fromId: user.id, fromName: user.name, fromAvatar: user.avatar,
      content, ts: new Date().toISOString(),
      reactions: [], replyTo: reply,
      edited: false, deleted: false,
      ...(activeConv.type === 'dm' ? { toId: activeConv.id, read: false } : { channelId: activeConv.id }),
    };
    setMessages(prev => [...prev, optimistic]);
    setReplyTo(null);
    try {
      const url = activeConv.type === 'channel' ? `/messages/channels/${activeConv.id}` : '/messages';
      const body = activeConv.type === 'channel'
        ? { content, replyTo: reply }
        : { toId: activeConv.id, content };
      const { data } = await api.post(url, body);
      setMessages(prev => prev.map(m => m.id === optimistic.id ? data : m));
      setLastTs(data.ts);
      loadSidebar();
    } catch {
      setMessages(prev => prev.filter(m => m.id !== optimistic.id));
      setInput(content);
    }
    setSending(false);
    inputRef.current?.focus();
  }, [input, activeConv, sending, replyTo, user, loadSidebar]);

  const toggleReaction = useCallback(async (messageId, emoji) => {
    try {
      const url = activeConv.type === 'channel'
        ? `/messages/channels/${activeConv.id}/${messageId}/react`
        : `/messages/react/${messageId}`;
      const { data } = await api.post(url, { emoji });
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, reactions: data.reactions } : m));
    } catch { }
  }, [activeConv]);

  const startEdit = useCallback((msg) => {
    setEditingId(msg.id);
    setEditContent(msg.content);
    setHoveredId(null);
  }, []);

  const saveEdit = useCallback(async () => {
    if (!editContent.trim() || !editingId || !activeConv) return;
    try {
      const url = activeConv.type === 'channel'
        ? `/messages/channels/${activeConv.id}/${editingId}`
        : `/messages/edit/${editingId}`;
      const { data } = await api.patch(url, { content: editContent.trim() });
      setMessages(prev => prev.map(m => m.id === editingId ? { ...m, content: data.content, edited: true } : m));
      setEditingId(null);
    } catch { }
  }, [editContent, editingId, activeConv]);

  const deleteMessage = useCallback(async (messageId) => {
    setHoveredId(null);
    try {
      const url = activeConv.type === 'channel'
        ? `/messages/channels/${activeConv.id}/${messageId}`
        : `/messages/delete/${messageId}`;
      await api.delete(url);
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, deleted: true, content: '' } : m));
      loadSidebar();
    } catch { }
  }, [activeConv, loadSidebar]);

  const sq = search.toLowerCase();
  const filteredContacts = contacts.filter(c => !sq || c.name.toLowerCase().includes(sq));
  const filteredChannels = channels.filter(c => !sq || c.name.toLowerCase().includes(sq));
  const filteredRecent = sq ? recent.filter(r => r.name.toLowerCase().includes(sq)) : recent;
  const isChannel = activeConv?.type === 'channel';
  const totalUnread = contacts.reduce((s, c) => s + (c.unreadCount || 0), 0) + channels.reduce((s, c) => s + (c.unreadCount || 0), 0);

  const isActive = (type, id) => activeConv?.type === type && activeConv?.id === id;

  return (
    <div style={{ margin: -24, height: 'calc(100vh - 52px)', display: 'flex', overflow: 'hidden', background: 'var(--bg)' }} onClick={() => setShowEmoji(false)}>

      {/* ── Sidebar ── */}
      <div style={{ width: 270, minWidth: 270, background: 'rgba(8,12,20,0.95)', borderRight: '1px solid var(--brd)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '12px 10px 8px', borderBottom: '1px solid var(--brd2)' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--txt)', marginBottom: 8, paddingLeft: 2 }}>
            💬 Messages {totalUnread > 0 && <span style={{ background: 'var(--j6)', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 9, marginLeft: 6 }}>{totalUnread}</span>}
          </div>
          <input
            style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--brd)', borderRadius: 6, padding: '6px 10px', color: 'var(--txt)', fontSize: 12, outline: 'none', boxSizing: 'border-box' }}
            placeholder="🔍  Search…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          {/* Recent */}
          <Section
            label={`Recent${filteredRecent.some(r => r.unreadCount > 0) ? ` · ${filteredRecent.reduce((s, r) => s + (r.unreadCount || 0), 0)} new` : ''}`}
            col={collapsed.recent}
            onToggle={() => setCollapsed(c => ({ ...c, recent: !c.recent }))}
          >
            {filteredRecent.length === 0 && (
              <div style={{ padding: '4px 12px 10px', fontSize: 11, color: 'var(--txt3)' }}>No recent conversations</div>
            )}
            {filteredRecent.map(r => (
              <SidebarItem
                key={r.type + r.id}
                icon={r.type === 'channel' ? r.icon : null}
                initials={r.type === 'dm' ? r.avatar : null}
                avatarData={r.type === 'dm' ? r.avatarData : null}
                iconColor={r.type === 'dm' ? TIER_COLOR[r.tier] : null}
                name={r.name}
                sub={r.lastMessage?.content?.replace(/\n/g, ' ').slice(0, 45) || ''}
                badge={r.unreadCount}
                time={r.lastMessage ? formatSidebarTime(r.lastMessage.ts) : ''}
                active={isActive(r.type, r.id)}
                onClick={() => openConv(r.type, r.id, r)}
              />
            ))}
          </Section>

          {/* Channels */}
          <Section
            label="Channels"
            col={collapsed.channels}
            onToggle={() => setCollapsed(c => ({ ...c, channels: !c.channels }))}
          >
            {filteredChannels.map(ch => (
              <SidebarItem
                key={ch.id}
                icon={ch.icon}
                name={ch.type === 'pod' ? ch.name.split('—')[0].trim() : ch.name}
                sub={ch.type === 'pod' ? ch.name.split('—').slice(1).join('—').trim() : ch.description?.slice(0, 40) || ''}
                badge={ch.unreadCount}
                active={isActive('channel', ch.id)}
                onClick={() => openConv('channel', ch.id, ch)}
              />
            ))}
          </Section>

          {/* Direct Messages */}
          <Section
            label="Direct Messages"
            col={collapsed.dms}
            onToggle={() => setCollapsed(c => ({ ...c, dms: !c.dms }))}
          >
            {filteredContacts.map(c => (
              <SidebarItem
                key={c.id}
                initials={c.avatar}
                avatarData={c.avatarData}
                iconColor={TIER_COLOR[c.tier]}
                name={c.name}
                sub={ROLE_LABEL[c.role] || c.role}
                badge={c.unreadCount}
                time={c.lastMessage ? formatSidebarTime(c.lastMessage.ts) : ''}
                active={isActive('dm', c.id)}
                onClick={() => openConv('dm', c.id, c)}
              />
            ))}
          </Section>
        </div>
      </div>

      {/* ── Main panel ── */}
      {!activeConv ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--txt3)', gap: 12 }}>
          <div style={{ fontSize: 52 }}>💬</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--txt)' }}>NeuralOps Messaging</div>
          <div style={{ fontSize: 13, textAlign: 'center', maxWidth: 340, lineHeight: 1.6 }}>
            Select a <strong style={{ color: 'var(--j1)' }}>channel</strong> for group discussions or a <strong style={{ color: 'var(--j1)' }}>team member</strong> for a direct message.
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            {channels.slice(0, 2).map(ch => (
              <button key={ch.id} onClick={() => openConv('channel', ch.id, ch)}
                style={{ background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: 8, padding: '8px 16px', color: 'var(--j1)', cursor: 'pointer', fontSize: 13 }}>
                {ch.icon} {ch.name}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Header */}
          <div style={{ height: 56, borderBottom: '1px solid var(--brd)', display: 'flex', alignItems: 'center', padding: '0 20px', gap: 12, flexShrink: 0, background: 'rgba(8,12,20,0.5)' }}>
            {isChannel ? (
              <>
                <span style={{ fontSize: 24 }}>{activeConv.data?.icon || '📢'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--txt)' }}>{activeConv.data?.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--txt3)' }}>
                    {activeConv.data?.channelType === 'pod' && activeConv.data?.memberNames?.length
                      ? `${activeConv.data.memberNames.slice(0, 5).join(', ')}${activeConv.data.memberNames.length > 5 ? ` +${activeConv.data.memberNames.length - 5}` : ''}`
                      : activeConv.data?.description?.slice(0, 80) || ''}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: 'var(--txt3)', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--brd)', borderRadius: 12, padding: '2px 10px' }}>
                  {activeConv.data?.channelType === 'pod' ? '🔬 Pod Channel' : activeConv.data?.channelType === 'announcements' ? '📣 Announcements' : '📢 General'}
                </div>
              </>
            ) : (
              <>
                <Avatar initials={activeConv.data?.avatar} avatarData={activeConv.data?.avatarData} size={36} color={TIER_COLOR[activeConv.data?.tier]} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--txt)' }}>{activeConv.data?.name}</div>
                  <div style={{ fontSize: 11, color: ROLE_COLOR[activeConv.data?.role] }}>
                    {ROLE_LABEL[activeConv.data?.role]}
                    {activeConv.data?.tier ? ` · ${activeConv.data.tier}` : ''}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 0' }}>
            {loading && <div style={{ textAlign: 'center', padding: 30, color: 'var(--txt3)', fontSize: 13 }}>Loading…</div>}
            {!loading && messages.length === 0 && (
              <div style={{ textAlign: 'center', padding: 48, color: 'var(--txt3)' }}>
                <div style={{ fontSize: 40, marginBottom: 10 }}>{isChannel ? activeConv.data?.icon || '📢' : '👋'}</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--txt2)', marginBottom: 6 }}>
                  {isChannel ? `Welcome to ${activeConv.data?.name}` : `Say hello to ${activeConv.data?.name}`}
                </div>
                <div style={{ fontSize: 12 }}>{isChannel ? activeConv.data?.description : 'Start a conversation — send your first message below.'}</div>
              </div>
            )}

            {messages.map((msg, i) => {
              const prev = messages[i - 1] || null;
              const grouped = isGrouped(msg, prev);
              const showDiv = needsDivider(msg, prev);
              const isMe = msg.fromId === user.id;
              const isHov = hoveredId === msg.id;
              const bubbleLeft = isChannel || !isMe;

              return (
                <div key={msg.id}>
                  {showDiv && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 20px 6px', userSelect: 'none' }}>
                      <div style={{ flex: 1, height: 1, background: 'var(--brd)' }} />
                      <span style={{ fontSize: 10, color: 'var(--txt3)', fontWeight: 600, letterSpacing: '0.05em' }}>{getDateLabel(msg.ts)}</span>
                      <div style={{ flex: 1, height: 1, background: 'var(--brd)' }} />
                    </div>
                  )}

                  <div
                    style={{ position: 'relative', padding: `${grouped ? 1 : 6}px 20px 1px`, display: 'flex', flexDirection: bubbleLeft ? 'row' : 'row-reverse', gap: 10, alignItems: 'flex-start' }}
                    onMouseEnter={() => setHoveredId(msg.id)}
                    onMouseLeave={() => setHoveredId(null)}
                  >
                    {/* Avatar */}
                    <div style={{ width: bubbleLeft ? 36 : 0, flexShrink: 0, paddingTop: 2 }}>
                      {bubbleLeft && !grouped && (
                        isChannel
                          ? <Avatar initials={msg.fromAvatar || '??'} avatarData={msg.fromAvatarData} size={34} />
                          : <Avatar initials={activeConv.data?.avatar} avatarData={activeConv.data?.avatarData} size={34} color={TIER_COLOR[activeConv.data?.tier]} />
                      )}
                    </div>

                    {/* Content block */}
                    <div style={{ maxWidth: '68%', minWidth: 0 }}>
                      {/* Sender + time header */}
                      {!grouped && (
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 3, flexDirection: bubbleLeft ? 'row' : 'row-reverse' }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: isMe ? 'var(--j1)' : 'var(--txt)' }}>
                            {isMe ? 'You' : (isChannel ? msg.fromName : activeConv.data?.name)}
                          </span>
                          <span style={{ fontSize: 10, color: 'var(--txt3)' }}>{formatTime(msg.ts)}</span>
                        </div>
                      )}

                      {/* Reply quote */}
                      {msg.replyTo && !msg.deleted && (
                        <div style={{ background: 'rgba(255,255,255,0.04)', borderLeft: '3px solid var(--j1)', borderRadius: '0 4px 4px 0', padding: '3px 8px', marginBottom: 4, fontSize: 11, color: 'var(--txt3)', maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <span style={{ color: 'var(--j1)', fontWeight: 600 }}>{msg.replyTo.fromName}: </span>
                          {msg.replyTo.content}
                        </div>
                      )}

                      {/* Message bubble / edit */}
                      {editingId === msg.id ? (
                        <div>
                          <textarea
                            value={editContent}
                            onChange={e => setEditContent(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEdit(); }
                              if (e.key === 'Escape') setEditingId(null);
                            }}
                            autoFocus
                            rows={2}
                            style={{ width: '100%', background: 'rgba(255,255,255,0.07)', border: '1px solid var(--j1)', borderRadius: 6, padding: '7px 10px', color: 'var(--txt)', fontSize: 13, resize: 'none', outline: 'none', minHeight: 56, boxSizing: 'border-box' }}
                          />
                          <div style={{ fontSize: 10, color: 'var(--txt3)', marginTop: 2 }}>Enter to save · Esc to cancel</div>
                        </div>
                      ) : (
                        <div style={{
                          background: msg.deleted ? 'transparent' : isMe && !isChannel ? 'rgba(0,212,255,0.12)' : 'rgba(255,255,255,0.06)',
                          border: `1px solid ${msg.deleted ? 'transparent' : isMe && !isChannel ? 'rgba(0,212,255,0.25)' : 'rgba(255,255,255,0.08)'}`,
                          borderRadius: isMe && !isChannel ? '12px 4px 12px 12px' : '4px 12px 12px 12px',
                          padding: msg.deleted ? 0 : '7px 12px',
                          fontSize: 13, lineHeight: 1.55, color: msg.deleted ? 'var(--txt3)' : 'var(--txt)',
                          wordBreak: 'break-word', fontStyle: msg.deleted ? 'italic' : 'normal',
                        }}>
                          {msg.deleted ? 'This message was deleted.' : msg.content}
                          {msg.edited && !msg.deleted && <span style={{ fontSize: 9, color: 'var(--txt3)', marginLeft: 6 }}>(edited)</span>}
                        </div>
                      )}

                      {/* Reactions */}
                      {!msg.deleted && (msg.reactions || []).filter(r => r.userIds?.length > 0).length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                          {(msg.reactions || []).filter(r => r.userIds?.length > 0).map(r => (
                            <button
                              key={r.emoji}
                              onClick={e => { e.stopPropagation(); toggleReaction(msg.id, r.emoji); }}
                              title={`${r.userIds.length} reaction${r.userIds.length > 1 ? 's' : ''}`}
                              style={{
                                background: r.userIds.includes(user.id) ? 'rgba(0,212,255,0.15)' : 'rgba(255,255,255,0.06)',
                                border: `1px solid ${r.userIds.includes(user.id) ? 'rgba(0,212,255,0.4)' : 'rgba(255,255,255,0.12)'}`,
                                borderRadius: 10, padding: '2px 7px', cursor: 'pointer', fontSize: 12,
                                display: 'flex', alignItems: 'center', gap: 4, color: 'var(--txt)',
                              }}
                            >
                              {r.emoji} <span style={{ fontSize: 10, color: 'var(--txt3)' }}>{r.userIds.length}</span>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* DM read receipt */}
                      {!isChannel && isMe && !msg.deleted && (
                        <div style={{ textAlign: 'right', fontSize: 9, color: msg.read ? '#22c55e' : 'var(--txt3)', marginTop: 2 }}>
                          {msg.read ? '✓✓' : '✓'}
                        </div>
                      )}
                    </div>

                    {/* Hover action bar */}
                    {isHov && !msg.deleted && editingId !== msg.id && (
                      <div
                        onClick={e => e.stopPropagation()}
                        style={{
                          position: 'absolute', top: 0,
                          ...(bubbleLeft ? { right: 16 } : { left: 16 }),
                          background: 'var(--bg3)', border: '1px solid var(--brd)',
                          borderRadius: 8, display: 'flex', alignItems: 'center', gap: 1,
                          padding: '3px 6px', zIndex: 20, boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
                        }}
                      >
                        {QUICK_REACTIONS.map(e => (
                          <button key={e} onClick={() => toggleReaction(msg.id, e)} title={`React ${e}`}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, padding: '2px 3px', borderRadius: 4, lineHeight: 1 }}>
                            {e}
                          </button>
                        ))}
                        <div style={{ width: 1, background: 'var(--brd)', height: 16, margin: '0 4px' }} />
                        <button onClick={() => setReplyTo({ ...msg, fromName: isMe ? 'You' : (isChannel ? msg.fromName : activeConv.data?.name) })}
                          title="Reply" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, padding: '2px 4px', color: 'var(--txt2)', borderRadius: 4 }}>↩️</button>
                        {msg.fromId === user.id && (
                          <>
                            <button onClick={() => startEdit(msg)} title="Edit"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, padding: '2px 4px', color: 'var(--txt2)', borderRadius: 4 }}>✏️</button>
                            <button onClick={() => deleteMessage(msg.id)} title="Delete"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, padding: '2px 4px', color: '#ef4444', borderRadius: 4 }}>🗑️</button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            <div ref={msgEndRef} />
          </div>

          {/* Input area */}
          <div style={{ borderTop: '1px solid var(--brd)', padding: '10px 16px', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
            {replyTo && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(0,212,255,0.07)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: '6px 6px 0 0', padding: '5px 10px', marginBottom: -1, fontSize: 11 }}>
                <span style={{ color: 'var(--j1)', flexShrink: 0 }}>↩️ {replyTo.fromName}:</span>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--txt3)' }}>{replyTo.content}</span>
                <button onClick={() => setReplyTo(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--txt3)', fontSize: 15, padding: '0 2px', lineHeight: 1 }}>×</button>
              </div>
            )}

            {showEmoji && (
              <div style={{ background: 'var(--bg3)', border: '1px solid var(--brd)', borderRadius: 10, marginBottom: 8, overflow: 'hidden', boxShadow: '0 -8px 24px rgba(0,0,0,0.5)' }}>
                <div style={{ display: 'flex', borderBottom: '1px solid var(--brd)' }}>
                  {EMOJI_GROUPS.map((g, i) => (
                    <button key={i} onClick={() => setEmojiTab(i)}
                      style={{ flex: 1, background: emojiTab === i ? 'rgba(0,212,255,0.1)' : 'none', border: 'none', borderBottom: `2px solid ${emojiTab === i ? 'var(--j1)' : 'transparent'}`, cursor: 'pointer', fontSize: 17, padding: '7px 0' }}>
                      {g.label}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10,1fr)', padding: '6px 4px', maxHeight: 130, overflowY: 'auto' }}>
                  {EMOJI_GROUPS[emojiTab].emojis.map(e => (
                    <button key={e} onClick={() => { setInput(p => p + e); setShowEmoji(false); inputRef.current?.focus(); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 19, padding: '4px 2px', borderRadius: 4, lineHeight: 1 }}>
                      {e}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <form onSubmit={sendMessage} style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
              <button type="button" onClick={e => { e.stopPropagation(); setShowEmoji(s => !s); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 21, padding: '6px 4px', color: showEmoji ? 'var(--j1)' : 'var(--txt3)', flexShrink: 0, lineHeight: 1 }}>
                😊
              </button>
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => {
                  setInput(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
                }}
                placeholder={isChannel
                  ? `Message #${activeConv.data?.name?.split('—')[0]?.trim() || 'channel'}…`
                  : `Message ${activeConv.data?.name?.split(' ')[0] || 'member'}…`}
                rows={1}
                style={{
                  flex: 1, background: 'rgba(255,255,255,0.05)',
                  border: '1px solid var(--brd)', borderRadius: replyTo ? '0 0 8px 8px' : 8,
                  padding: '8px 12px', color: 'var(--txt)', fontSize: 13,
                  resize: 'none', outline: 'none', lineHeight: 1.5, maxHeight: 120,
                  overflow: 'auto', boxSizing: 'border-box',
                }}
              />
              <button
                type="submit"
                disabled={!input.trim() || sending}
                style={{
                  width: 36, height: 36, borderRadius: '50%', border: 'none', flexShrink: 0,
                  background: input.trim() ? 'var(--j1)' : 'rgba(255,255,255,0.08)',
                  color: input.trim() ? '#000' : 'var(--txt3)',
                  cursor: input.trim() ? 'pointer' : 'default', fontSize: 14,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.15s',
                }}
              >
                ➤
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
